import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  ColorInput,
  GridDropdown,
  LabelHover,
  EnhancedSlider,
} from '../../utils/gridUtils';
import { handleUpdateHotSpot } from '../gridpagefunction/HotSpot';
import { HotSpotInitialData } from '../../utils/constant';
import store from '../../redux/store';

const GridInput = ({ label, value, onChange, type = 'text', step }) => (
  <div className="flex items-center justify-between py-1 text-xs w-full">
    <LabelHover label={label} />
    <input
      type={type}
      value={value || ''}
      onChange={onChange}
      step={step}
      className="bg-[#2b2c2f] text-gray-200 px-1 py-1 rounded w-[130px] text-left text-[10px]
                       border-none focus:outline-none"
    />
  </div>
);

const GridDropRow = ({ label, value, onChange, options }) => (
  <div className="flex items-center justify-between py-1 text-xs w-full">
    <LabelHover label={label} />
    <div className="w-[130px]">
      <GridDropdown value={value} onChange={onChange} options={options} />
    </div>
  </div>
);

const GridTextarea = ({ label, value, onChange }) => (
  <div className="flex items-start justify-between py-1 text-xs w-full">
    <div className="flex items-start pt-1">
      <LabelHover label={label} />
    </div>
    <textarea
      value={value || ''}
      onChange={onChange}
      rows={1}
      className="bg-[#2b2c2f] text-gray-200 px-1 py-1 rounded w-[130px] text-left text-[10px]
                       border-none focus:outline-none resize-none"
    />
  </div>
);

const textFromHotspot = (value) => (typeof value === 'string' ? value : '');

const getHotspotObjectId = (object) =>
  object?.name || object?._id || object?.url || object?.uuid || null;

const buildHotspotFormData = (hotspot) => ({
  buttonShape: hotspot.buttonShape ?? HotSpotInitialData.buttonShape,
  buttonText: textFromHotspot(hotspot.buttonText),
  buttonColor: hotspot.buttonColor ?? HotSpotInitialData.buttonColor,
  buttonTextColor: hotspot.buttonTextColor ?? HotSpotInitialData.buttonTextColor,
  buttonOpacity: hotspot.buttonOpacity ?? HotSpotInitialData.buttonOpacity,
  autoFocus: !!hotspot.autoFocus,
  functionType: hotspot.functionType ?? HotSpotInitialData.functionType,
  messageShape: hotspot.messageShape ?? HotSpotInitialData.messageShape,
  messageText: textFromHotspot(hotspot.messageText),
  messageBgColor: hotspot.messageBgColor ?? HotSpotInitialData.messageBgColor,
  messageTextColor:
    hotspot.messageTextColor ?? HotSpotInitialData.messageTextColor,
  messageOpacity: hotspot.messageOpacity ?? HotSpotInitialData.messageOpacity,
  messagePopupType:
    hotspot.messagePopupType ?? HotSpotInitialData.messagePopupType,
  focusDistance: hotspot.focusDistance ?? HotSpotInitialData.focusDistance,
});

/** Resolve saved hotspot fields for one scene object (userData, then Redux assets). */
export function resolveHotspotDataForObject(object) {
  if (!object) return null;

  if (object.userData?.hotspot) {
    return object.userData.hotspot;
  }

  const objectId = getHotspotObjectId(object);
  if (!objectId) return null;

  const assets = store.getState().assets_object?.assets_data?.hotspot || [];
  const fromAssets = assets.find((asset) => {
    const assetId =
      asset._id ?? asset.id ?? asset.url ?? asset.name ?? asset.temp_file_name;
    return assetId === objectId;
  });
  if (fromAssets) return fromAssets;

  const sceneHotspots = store.getState().assets_object?.sceneHotspots || [];
  const fromSceneList = sceneHotspots.find((hp) => {
    const hpId = hp?.name || hp?._id || hp?.url;
    return hpId === objectId;
  });
  if (fromSceneList?.userData?.hotspot) {
    return fromSceneList.userData.hotspot;
  }

  return null;
}

const HotSpotComponent = ({
  onBack,
  selectedObject,
  scene,
  camera,
  renderer,
  historyUiRevision = 0,
}) => {
  const { select_object } = useSelector((state) => state.assets_object);
  const activeObject = select_object || selectedObject;
  const activeObjectId = getHotspotObjectId(activeObject);

  const [data, setData] = useState(HotSpotInitialData);
  const [previewMessageOpacity, setPreviewMessageOpacity] = useState(false);

  const pushHotspotUpdate = useCallback(
    (nextData, previewOpacity = previewMessageOpacity) => {
      if (!activeObject?.userData?.isHotspot) return;
      handleUpdateHotSpot(activeObject, nextData, {
        scene,
        camera,
        renderer,
        previewMessageOpacity: previewOpacity,
      });
    },
    [activeObject, scene, camera, renderer, previewMessageOpacity]
  );

  // Load individual hotspot properties when selection changes only.
  useEffect(() => {
    setPreviewMessageOpacity(false);

    if (!activeObjectId || !activeObject?.userData?.isHotspot) {
      setData({ ...HotSpotInitialData });
      return;
    }

    const saved = resolveHotspotDataForObject(activeObject);
    if (saved) {
      setData(buildHotspotFormData(saved));
    } else {
      setData({ ...HotSpotInitialData });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeObjectId, historyUiRevision]);

  const handleChange = (field, value) => {
    setData((prev) => {
      const next = { ...prev, [field]: value };
      pushHotspotUpdate(next);
      return next;
    });
  };

  return (
    <div className="text-xs flex flex-col py-2">
      <GridDropRow
        label="Button Shape"
        value={data.buttonShape}
        onChange={(e) => handleChange('buttonShape', e.target.value)}
        options={[
          { value: 'Circle', label: 'Circle' },
          { value: 'Rectangle', label: 'Rectangle' },
        ]}
      />

      <GridTextarea
        label="Button Text"
        value={data.buttonText}
        onChange={(e) => handleChange('buttonText', e.target.value)}
      />

      <div className="py-1">
        <ColorInput
          label="Button Color"
          color={data.buttonColor}
          setColor={(value) => handleChange('buttonColor', value)}
        />
      </div>
      <div className="py-1">
        <ColorInput
          label="Button Text Color"
          color={data.buttonTextColor}
          setColor={(value) => handleChange('buttonTextColor', value)}
        />
      </div>
      <div className="py-1">
        <EnhancedSlider
          toFix={2}
          label="Button Opacity"
          value={Number(data.buttonOpacity)}
          onChange={(val) => handleChange('buttonOpacity', val)}
          min={0}
          max={1}
          step={0.01}
          unit=""
        />
      </div>

      <div className="border-t border-gray-700 my-1.5 opacity-50" />

      <GridDropRow
        label="Function Type"
        value={data.functionType}
        onChange={(e) => handleChange('functionType', e.target.value)}
        options={[
          { value: 'Hover', label: 'Hover' },
          { value: 'Click', label: 'Click' },
        ]}
      />

      <GridDropRow
        label="Message Shape"
        value={data.messageShape}
        onChange={(e) => handleChange('messageShape', e.target.value)}
        options={[{ value: 'Plane', label: 'Plane' }]}
      />

      <GridTextarea
        label="Message Text"
        value={data.messageText}
        onChange={(e) => handleChange('messageText', e.target.value)}
      />

      <div className="py-1">
        <ColorInput
          label="Message BG Color"
          color={data.messageBgColor}
          setColor={(value) => handleChange('messageBgColor', value)}
        />
      </div>
      <div className="py-1">
        <ColorInput
          label="Message Text Color"
          color={data.messageTextColor}
          setColor={(value) => handleChange('messageTextColor', value)}
        />
      </div>
      <div className="py-1">
        <EnhancedSlider
          toFix={2}
          label="Message Opacity"
          value={Number(data.messageOpacity)}
          onChange={(val) => {
            setPreviewMessageOpacity(true);
            setData((prev) => {
              const next = { ...prev, messageOpacity: val };
              pushHotspotUpdate(next, true);
              return next;
            });
          }}
          min={0}
          max={1}
          step={0.01}
          unit=""
        />
      </div>

      <GridDropRow
        label="Popup Type"
        value={data.messagePopupType}
        onChange={(e) => handleChange('messagePopupType', e.target.value)}
        options={[
          { value: 'fadeIn', label: 'Fade In' },
          { value: 'normal', label: 'Normal' },
        ]}
      />

      <div className="py-1">
        <EnhancedSlider
          toFix={1}
          label="Focus Distance"
          value={Number(data.focusDistance ?? 10)}
          onChange={(val) => handleChange('focusDistance', val)}
          min={1}
          max={50}
          step={0.5}
          unit=""
        />
      </div>
    </div>
  );
};

export default HotSpotComponent;
